import { t } from "../lib/i18n";
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import Tooltip from './Tooltip';
import LoadingState from './LoadingState';
import EmptyState from './EmptyState';
import ConfirmModal from './ConfirmModal';

interface Property {
  id: string;
  custom_id?: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  property_type: string;
  square_footage: number;
  number_of_units: number;
  purchase_price: number;
  current_value: number;
  year_built: number;
  description?: string;
}

export const Properties: React.FC = () => {
  const [properties, setProperties] = useState<Property[]>([]);
  const [newProperty, setNewProperty] = useState<Partial<Property>>({});
  const [message, setMessage] = useState('');
  const [editingPropertyId, setEditingPropertyId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [propertyToDelete, setPropertyToDelete] = useState<string | null>(null);
  const [isUploadingOM, setIsUploadingOM] = useState(false);

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/properties');
      // Sort properties by ID in descending order to show newest first
      const sortedProperties = response.data.sort((a: Property, b: Property) => b.id.localeCompare(a.id));
      setProperties(sortedProperties);
    } catch (err: any) {
      console.error('Error fetching properties:', err.response?.data || err.message);
      setError(typeof err.response?.data?.detail === 'object' ? JSON.stringify(err.response.data.detail) : err.response?.data?.detail || 'Error fetching properties.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddProperty = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage('');
    try {
      await api.post('/properties', newProperty);
      setMessage('Property added successfully!');
      setNewProperty({});
      fetchProperties();
    } catch (error: any) {
      console.error('Error adding property:', error.response?.data || error.message);
      setMessage(typeof error.response?.data?.detail === 'object' ? JSON.stringify(error.response.data.detail) : error.response?.data?.detail || 'Error adding property.');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setNewProperty(prev => ({ ...prev, [name]: value }));
  };

  const handleEditClick = (property: Property) => {
    setEditingPropertyId(property.id);
    setNewProperty(property); // Populate form with property data for editing
    setMessage('');
  };

  const cancelEdit = () => {
    setEditingPropertyId(null);
    setNewProperty({}); // Clear form
    setMessage('');
  };

  const handleUpdateProperty = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingPropertyId) return;
    setMessage('');
    try {
      await api.put(`/properties/${editingPropertyId}`, newProperty);
      setMessage('Property updated successfully!');
      setEditingPropertyId(null);
      setNewProperty({});
      fetchProperties();
    } catch (error: any) {
      console.error('Error updating property:', error.response?.data || error.message);
      setMessage(typeof error.response?.data?.detail === 'object' ? JSON.stringify(error.response.data.detail) : error.response?.data?.detail || 'Error updating property.');
    }
  };

  const handleDeleteProperty = (propertyId: string) => {
    setPropertyToDelete(propertyId);
  };

  const confirmDeleteProperty = async () => {
    if (!propertyToDelete) return;
    setMessage('');
    try {
      await api.delete(`/properties/${propertyToDelete}`);
      setMessage('Property deleted successfully!');
      fetchProperties();
    } catch (error: any) {
      console.error('Error deleting property:', error.response?.data || error.message);
      setMessage(typeof error.response?.data?.detail === 'object' ? JSON.stringify(error.response.data.detail) : error.response?.data?.detail || 'Error deleting property.');
    } finally {
      setPropertyToDelete(null);
    }
  };

  const handleOMUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploadingOM(true);
    setMessage('');
    const formData = new FormData();
    formData.append('file', file);

    try {
      await api.post('/properties/upload-om', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setMessage('OM processed and property created successfully!');
      fetchProperties();
    } catch (error: any) {
      console.error('Error uploading OM:', error.response?.data || error.message);
      setMessage(typeof error.response?.data?.detail === 'object' ? JSON.stringify(error.response.data.detail) : error.response?.data?.detail || 'Error uploading OM.');
    } finally {
      setIsUploadingOM(false);
      if (e.target) {
        e.target.value = '';
      }
    }
  };

  if (loading) {
    return <LoadingState message="Loading properties..." fullScreen />;
  }

  if (error) {
    return <div className="container mx-auto p-4 text-danger">{t("properties.k1")} {error}</div>;
  }



  return (
    <div className="container mx-auto p-4 bg-bg-primary min-h-screen">
      <div className="flex items-center mb-4">
        <h2 className="text-2xl font-bold text-text-secondary">{t("properties.k2")}</h2>
        <Tooltip text="This page allows you to manage your properties. You can add, edit, and delete properties, as well as view key details for each one. Adding properties here unlocks powerful features like Market Analysis, Valuation, and Predictive Trends." />
      </div>

      {message && <p className={message.includes('successfully') ? 'text-success mb-4' : 'text-danger mb-4'}>{message}</p>}
      
      {isUploadingOM && (
        <div className="mb-6 p-4 bg-blue-50 border-blue-200 border rounded flex items-center justify-center">
          <LoadingState message="Extracting property details from OM..." />
        </div>
      )}

      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-bold text-text-secondary">{editingPropertyId ? t("properties.k3") : t("properties.k4")}</h3>
        {!editingPropertyId && (
          <div className="relative">
            <input 
              type="file" 
              accept=".pdf" 
              onChange={handleOMUpload} 
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              disabled={isUploadingOM}
            />
            <button className="bg-primary text-white px-4 py-2 rounded hover:bg-primary-dark shadow flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
              {t("properties.k5")} </button>
          </div>
        )}
      </div>

      <form onSubmit={editingPropertyId ? handleUpdateProperty : handleAddProperty} className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 bg-bg-secondary p-4 rounded shadow border border-border-primary">
        {editingPropertyId && (
          <div className="md:col-span-2">
            <label htmlFor="systemId" className="block text-sm font-medium text-text-secondary mb-1">{t("properties.k6")}</label>
            <input
              type="text"
              id="systemId"
              value={newProperty.id || ''}
              readOnly
              className="border border-border-primary p-2 rounded w-full bg-gray-100 cursor-not-allowed"
            />
          </div>
        )}
        <input type="text" name="address" placeholder={t("properties.k7")} value={newProperty.address || ''} onChange={handleInputChange} required className="border border-border-primary p-2 rounded w-full" />
        <input type="text" name="city" placeholder={t("properties.k8")} value={newProperty.city || ''} onChange={handleInputChange} required className="border border-border-primary p-2 rounded w-full" />
        <input type="text" name="state" placeholder={t("properties.k9")} value={newProperty.state || ''} onChange={handleInputChange} required className="border border-border-primary p-2 rounded w-full" />
        <input type="text" name="zip_code" placeholder={t("properties.k10")} value={newProperty.zip_code || ''} onChange={handleInputChange} required className="border border-border-primary p-2 rounded w-full" />
        <input type="text" name="property_type" placeholder={t("properties.k11")} value={newProperty.property_type || ''} onChange={handleInputChange} required className="border border-border-primary p-2 rounded w-full" />
        <input type="text" name="custom_id" placeholder={t("properties.k12")} value={newProperty.custom_id || ''} onChange={handleInputChange} className="border border-border-primary p-2 rounded w-full" />
        <input type="number" name="square_footage" placeholder={t("properties.k13")} value={newProperty.square_footage || ''} onChange={handleInputChange} required className="border border-border-primary p-2 rounded w-full" />
        <input type="number" name="number_of_units" placeholder={t("properties.k14")} value={newProperty.number_of_units || ''} onChange={handleInputChange} required className="border border-border-primary p-2 rounded w-full" />
        <input type="number" name="purchase_price" placeholder={t("properties.k15")} value={newProperty.purchase_price || ''} onChange={handleInputChange} required className="border border-border-primary p-2 rounded w-full" />
        <input type="number" name="current_value" placeholder={t("properties.k16")} value={newProperty.current_value || ''} onChange={handleInputChange} required className="border border-border-primary p-2 rounded w-full" />
        <input type="number" name="year_built" placeholder={t("properties.k17")} value={newProperty.year_built || ''} onChange={handleInputChange} required className="border border-border-primary p-2 rounded w-full" />
        <textarea name="description" placeholder={t("properties.k18")} value={newProperty.description || ''} onChange={handleInputChange} className="col-span-full border border-border-primary p-2 rounded w-full"></textarea>
        <button type="submit" className="col-span-full bg-primary text-white px-4 py-2 rounded hover:bg-primary-dark">
          {editingPropertyId ? t("properties.k19") : t("properties.k20")}
        </button>
        {editingPropertyId && (
          <button type="button" onClick={cancelEdit} className="col-span-full bg-transparent border border-border-primary text-text-primary px-4 py-2 rounded hover:bg-bg-tertiary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-border-primary">
            {t("properties.k21")} </button>
        )}
      </form>

      <h3 className="text-xl font-bold mb-4 text-text-secondary">{t("properties.k22")}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {properties.length === 0 ? (
          <div className="col-span-full">
            <EmptyState 
              title={t("properties.k23")}
              description="Add your first property above to unlock powerful features like Market Analysis, Valuation, and Predictive Trends!"
              icon={<svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>}
            />
          </div>
        ) : (
          properties.map((property) => (
            <div key={property.id} className="bg-bg-secondary p-4 rounded shadow border border-border-primary">
              <Link to={`/properties/${property.id}`} className="block text-xl font-bold mb-2 text-blue-600 hover:underline">
                <h3>{property.address}, {property.city}</h3>
              </Link>
              
              <p className="text-text-secondary"><strong>{t("properties.k24")}</strong> {property.property_type}</p>
              <p className="text-text-secondary"><strong>{t("properties.k25")}</strong> {property.square_footage} {t("properties.k26")}</p>
              <p className="text-text-secondary"><strong>{t("properties.k27")}</strong> {property.number_of_units}</p>
              <p className="text-text-secondary"><strong>{t("properties.k28")}</strong> ${property.purchase_price?.toLocaleString()}</p>
              <p className="text-text-secondary"><strong>{t("properties.k29")}</strong> ${property.current_value?.toLocaleString()}</p>
              <p className="text-text-secondary"><strong>{t("properties.k30")}</strong> {property.year_built}</p>
              {property.description && <p className="text-text-secondary"><strong>{t("properties.k31")}</strong> {property.description}</p>}
              <div className="mt-4 flex flex-wrap gap-2">
                <button onClick={() => handleEditClick(property)} className="bg-success text-white px-4 py-2 rounded hover:bg-success-dark">{t("properties.k32")}</button>
                <button onClick={() => handleDeleteProperty(property.id)} className="bg-danger text-white px-4 py-2 rounded hover:bg-red-700">{t("properties.k33")}</button>
                <Link to={`/properties/${property.id}/market-analysis`} className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 no-underline">{t("properties.k34")}</Link>
                <Link to={`/properties/${property.id}/valuation`} className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 no-underline">{t("properties.k35")}</Link>
                <Link to={`/properties/${property.id}/trends`} className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 no-underline">{t("properties.k36")}</Link>
              </div>
            </div>
          ))
        )}
      </div>
      <ConfirmModal
        isOpen={!!propertyToDelete}
        title={t("properties.k37")}
        message="Are you sure you want to delete this property? This action cannot be undone."
        onConfirm={confirmDeleteProperty}
        onCancel={() => setPropertyToDelete(null)}
        confirmText="Delete Property"
      />
    </div>
  );
};

